import { brandClass } from './chunk-N7SZ66QO.js';

// src/errors/index.ts
var ErrorCategory = {
  VALIDATION: "validation",
  AUTHENTICATION: "authentication",
  AUTHORIZATION: "authorization",
  NOT_FOUND: "not_found",
  CONFLICT: "conflict",
  RATE_LIMIT: "rate_limit",
  EXTERNAL_SERVICE: "external_service",
  DATABASE: "database",
  CONFIGURATION: "configuration",
  INTERNAL: "internal",
  DATA: "data",
  PIPELINE: "pipeline"
};
var DOCS_BASE = "https://docs.authe.io/errors";
function spec(code, description, userAction, category, defaultStatus, opts = {}) {
  return {
    code,
    description,
    userAction,
    documentationUrl: `${DOCS_BASE}/${code}`,
    category,
    defaultStatus,
    retryable: opts.retryable ?? false,
    severity: opts.severity ?? "ERROR"
  };
}
var ErrorCode = {
  // Validation
  VAL001: spec("VAL001", "Missing required field", "Provide all required fields and retry the request.", ErrorCategory.VALIDATION, 400, { severity: "WARNING" }),
  VAL002: spec("VAL002", "Invalid field format", "Check the field format documentation and correct the value.", ErrorCategory.VALIDATION, 400, { severity: "WARNING" }),
  VAL003: spec("VAL003", "Invalid field value", "Use one of the allowed values listed in the documentation.", ErrorCategory.VALIDATION, 400, { severity: "WARNING" }),
  VAL004: spec("VAL004", "Invalid response structure", "An internal response could not be parsed; retry, then contact support if persistent.", ErrorCategory.VALIDATION, 500),
  // Authentication
  AUTH001: spec("AUTH001", "Invalid credentials", "Sign in again with valid credentials.", ErrorCategory.AUTHENTICATION, 401),
  AUTH002: spec("AUTH002", "Token expired", "Refresh your session and retry.", ErrorCategory.AUTHENTICATION, 401),
  AUTH003: spec("AUTH003", "Invalid token", "Sign out, sign back in, and retry.", ErrorCategory.AUTHENTICATION, 401),
  // Authorization
  PERM001: spec("PERM001", "Insufficient permissions", "Ask an admin to grant the required permission, or use a different account.", ErrorCategory.AUTHORIZATION, 403),
  // NOT a tenancy refusal, despite what this line used to say ("You do not have
  // access to this resource within your tenant"). PERM002 is the authorizer's
  // general deny: a wrong audience, an app/env or application-type mismatch, an
  // expired or incomplete impersonation session, a revoked API key, an AWS
  // account that is not allowed, a blue-route mismatch, an unregistered public
  // API, or a gateway not whitelisted for the calling application. None of those
  // is tenancy — though a handler-level cross-tenant refusal carries PERM002
  // too, so tenancy is one cause among many and the line can no longer name
  // just that one. Mirrors `commons.errors.codes`.
  PERM002: spec("PERM002", "Resource access denied", "Refused before the service ran. Signing in again clears an expired or mismatched session; otherwise the resource belongs to another tenant, or this application isn't registered to reach that API.", ErrorCategory.AUTHORIZATION, 403),
  // Packaging, not permission. PERM001's remedy ("ask an admin to grant the
  // required permission") is actively wrong for a plan refusal: nobody in the
  // customer's own org can grant an entitlement their subscription lacks.
  PERM003: spec("PERM003", "Not included in your plan", "This feature is not part of your current subscription. Contact sales to add it.", ErrorCategory.AUTHORIZATION, 403),
  // A cap, not an absence. PERM003 says the feature is not yours; this says it
  // is, and it is full — so the remedy is to free a slot or move tier, never to
  // retry (which is why this is not the 429 RATE001).
  PERM004: spec("PERM004", "Plan limit reached", "Remove an existing item to free a slot, or upgrade for a higher limit.", ErrorCategory.AUTHORIZATION, 403),
  // The session's access has ENDED: its organization membership was removed or
  // deactivated, or its customer was suspended or archived (a destroyed demo
  // too). Not PERM001 — no admin of an org the reader has left can grant
  // anything — and not PERM002, whose "sign in again" cannot restore a
  // membership. Every request is refused from then on, so a host stops the app
  // and says so once instead of rendering an error per panel. Mirrors
  // `commons.errors.codes`.
  PERM005: spec("PERM005", "Access ended", "Your access to this organization has ended: your membership was removed or the organization was closed. Sign out; to regain access, ask an admin of the organization to invite you again.", ErrorCategory.AUTHORIZATION, 403, { severity: "WARNING" }),
  // Resource
  RES001: spec("RES001", "Resource not found", "Verify the identifier; the resource may have been deleted or never existed.", ErrorCategory.NOT_FOUND, 404),
  RES002: spec("RES002", "Resource already exists", "Use a different identifier or update the existing resource instead.", ErrorCategory.CONFLICT, 409, { severity: "WARNING" }),
  // Rate limiting
  RATE001: spec("RATE001", "Rate limit exceeded", "Slow request rate and retry after the `Retry-After-Ms` interval.", ErrorCategory.RATE_LIMIT, 429, { retryable: true, severity: "WARNING" }),
  // A plan's usage quota is used up for its window. 429, but NOT retryable: an
  // unknown 429 is retried by the client, and re-sending a refused run cannot
  // clear a monthly cap. `details.resets_at` says when it does.
  QUOTA001: spec("QUOTA001", "Usage quota reached", "Wait until the quota resets, or move to a plan with a higher quota. The message says which quota and when it resets.", ErrorCategory.RATE_LIMIT, 429, { severity: "WARNING" }),
  // External service
  EXT001: spec("EXT001", "External service unavailable", "An upstream provider is unavailable; retry the request, then contact support if persistent.", ErrorCategory.EXTERNAL_SERVICE, 503, { retryable: true }),
  EXT002: spec("EXT002", "External service timeout", "An upstream provider timed out; retry, then contact support if persistent.", ErrorCategory.EXTERNAL_SERVICE, 504, { retryable: true }),
  // Database
  DB001: spec("DB001", "Database connection error", "Retry the request; if persistent, contact support.", ErrorCategory.DATABASE, 502, { retryable: true }),
  DB002: spec("DB002", "Database query error", "An internal query failed; retry and contact support if persistent.", ErrorCategory.DATABASE, 500),
  DB003: spec("DB003", "Database throttled", "Capacity throttled the request; retry after `Retry-After-Ms`.", ErrorCategory.DATABASE, 429, { retryable: true, severity: "WARNING" }),
  // A read stopped because it ran out of the time its request had. 504, but NOT
  // retryable: the client retries a retryable code by itself, and re-sending the
  // same read spends the same wait again. The remedy is the reader's.
  DB004: spec("DB004", "Query ran out of time", "Narrow the time range or add a filter, then try again.", ErrorCategory.DATABASE, 504, { severity: "WARNING" }),
  // Configuration / Internal
  CFG001: spec("CFG001", "Service configuration error", "An internal misconfiguration was detected; this is a server-side bug. Contact support.", ErrorCategory.CONFIGURATION, 500),
  INT001: spec("INT001", "Internal server error", "An unexpected error occurred. Retry, then contact support if persistent.", ErrorCategory.INTERNAL, 500),
  // Pipeline + data
  PIPE001: spec("PIPE001", "Pipeline processing error", "A pipeline step failed. The transaction will be retried automatically; contact support if it does not recover.", ErrorCategory.PIPELINE, 500, { retryable: true }),
  DATA001: spec("DATA001", "Data integrity error", "An expected invariant on the data was violated. This is a server-side bug; contact support.", ErrorCategory.DATA, 500)
};
function getCodeSpec(code) {
  for (const member of Object.values(ErrorCode)) {
    if (member.code === code) return member;
  }
  return null;
}
function errorCodeOf(error) {
  for (const candidate of [error.code, error.error_code]) {
    if (candidate && getCodeSpec(candidate)) return candidate;
  }
  return error.error_code ?? null;
}
function missingScopesOf(error) {
  if (!error || typeof error !== "object") return [];
  const raw = error.missing_scopes;
  if (!Array.isArray(raw)) return [];
  return [...new Set(raw.filter((s) => typeof s === "string" && s.trim() !== "").map((s) => s.trim()))];
}
function allCodeSpecs() {
  const out = {};
  for (const member of Object.values(ErrorCode)) out[member.code] = member;
  return out;
}
var AutheError = class _AutheError extends Error {
  static {
    brandClass(this, "errors/AutheError");
  }
  spec;
  details;
  correlationId;
  constructor(spec2, opts = {}) {
    super(opts.message ?? spec2.description);
    this.name = "AutheError";
    this.spec = spec2;
    this.details = opts.details ?? {};
    this.correlationId = opts.correlationId;
    if (opts.cause !== void 0) this.cause = opts.cause;
  }
  /** Look up a code by string id and build an `AutheError` (falls back to INT001). */
  static fromCode(code, opts = {}) {
    return new _AutheError(getCodeSpec(code) ?? ErrorCode.INT001, opts);
  }
  get retryable() {
    return this.spec.retryable;
  }
  toBody() {
    return {
      message: this.message,
      error_type: this.name,
      error_code: this.spec.code,
      error_level: this.spec.severity,
      details: this.details,
      correlation_id: this.correlationId ?? null
    };
  }
  toResponse() {
    return { statusCode: this.spec.defaultStatus, ok: false, error: this.toBody() };
  }
};
var STATUS_FALLBACK = [
  [401, ErrorCode.AUTH001],
  [403, ErrorCode.PERM001],
  [404, ErrorCode.RES001],
  [409, ErrorCode.RES002],
  [429, ErrorCode.RATE001]
];
function withCatalogCode(response) {
  const code = errorCodeOf(response.error);
  if (!code || code === response.error.error_code) return response;
  return { ...response, error: { ...response.error, error_code: code } };
}
function withStatusCode(response) {
  if (response.error.error_code) return response;
  const spec2 = STATUS_FALLBACK.find(([status]) => status === response.statusCode)?.[1];
  if (!spec2) return response;
  return {
    ...response,
    error: {
      ...response.error,
      error_code: spec2.code,
      error_level: response.error.error_level ?? spec2.severity
    }
  };
}
function isServerError(err) {
  if (err instanceof AutheError) return true;
  if (!err || typeof err !== "object") return false;
  if (err.ok === false) return true;
  const envelope = err.envelope;
  if (envelope && typeof envelope === "object" && envelope.ok === false) {
    return true;
  }
  if (graphqlErrorInfoOf(err)) return true;
  return typeof err.statusCode === "number";
}
function missingEntitlementsOf(error) {
  if (error == null) return null;
  const body = toErrorResponse(error).error;
  if (errorCodeOf(body) !== ErrorCode.PERM003?.code) return null;
  const details = body.details;
  const missing = details && typeof details === "object" ? details.missing_entitlements : void 0;
  return Array.isArray(missing) ? missing.filter((m) => typeof m === "string") : null;
}
function isPlanRefusalFor(error, entitlement) {
  return missingEntitlementsOf(error)?.includes(entitlement) ?? false;
}
function toErrorResponse(err) {
  if (err instanceof AutheError) return err.toResponse();
  if (err && typeof err === "object" && "ok" in err && err.ok === false) {
    return withCatalogCode(err);
  }
  if (err && typeof err === "object" && "envelope" in err) {
    const envelope = err.envelope;
    if (envelope && typeof envelope === "object" && envelope.ok === false) {
      return withStatusCode(withCatalogCode(envelope));
    }
  }
  const info = graphqlErrorInfoOf(err);
  if (info) {
    const response = withCatalogCode({
      statusCode: 0,
      ok: false,
      error: { details: {}, ...info, correlation_id: info.correlation_id ?? correlationIdOf(err) }
    });
    const spec3 = ErrorCode[response.error.error_code];
    return { ...response, statusCode: spec3?.defaultStatus ?? 0 };
  }
  const message = err instanceof Error ? err.message : String(err);
  const status = err && typeof err === "object" ? err.statusCode : void 0;
  const mapped = typeof status === "number" ? STATUS_FALLBACK.find(([code]) => code === status)?.[1] : void 0;
  const spec2 = mapped ?? ErrorCode.INT001;
  return {
    statusCode: spec2.defaultStatus,
    ok: false,
    error: {
      message,
      error_type: err instanceof Error ? err.name : "Error",
      error_code: spec2.code,
      error_level: spec2.severity,
      details: {},
      correlation_id: correlationIdOf(err)
    }
  };
}
function graphqlErrorInfoOf(err) {
  if (!err || typeof err !== "object") return null;
  const errors = err.errors;
  if (!Array.isArray(errors)) return null;
  for (const entry of errors) {
    const info = entry && typeof entry === "object" ? entry.errorInfo : null;
    if (!info || typeof info !== "object") continue;
    const { code, error_code: errorCode, message } = info;
    if (typeof code !== "string" && typeof errorCode !== "string") continue;
    return {
      ...info,
      message: typeof message === "string" ? message : String(entry.message ?? "")
    };
  }
  return null;
}
function correlationIdOf(err) {
  if (!err || typeof err !== "object") return null;
  const value = err.correlationId;
  return typeof value === "string" && value.length > 0 ? value : null;
}
function specForError(err) {
  const code = toErrorResponse(err).error.error_code;
  return code ? getCodeSpec(code) : null;
}
function retryHelpsReader(err) {
  return retryCanSucceed(specForError(err));
}
function retryCanSucceed(spec2) {
  return spec2 === null || spec2.retryable || spec2.defaultStatus >= 500;
}
function remedyForReader(err) {
  const spec2 = specForError(err);
  return {
    retryable: retryCanSucceed(spec2),
    action: (spec2 ?? ErrorCode.INT001).userAction
  };
}

export { AutheError, ErrorCategory, ErrorCode, allCodeSpecs, errorCodeOf, getCodeSpec, isPlanRefusalFor, isServerError, missingEntitlementsOf, missingScopesOf, remedyForReader, retryHelpsReader, specForError, toErrorResponse };
//# sourceMappingURL=chunk-HCZGW64N.js.map
//# sourceMappingURL=chunk-HCZGW64N.js.map